/**
 * BUG-S2103-002 — ลบหลายรายการด้วย Promise.all: รายการแรกที่ล้มทำให้ทั้งก้อน reject ทันที
 * → หน้าแจ้งแค่ "ไม่สามารถลบ…บางรายการได้" ไม่บอกว่ารายการไหน/เพราะอะไร · ไม่ refetch
 * ตาราง (แถวที่ลบไปแล้วยังโชว์) · แถวที่ลบแล้วยังถูกเลือกค้าง กดลบซ้ำได้ 404
 * helper นี้รอครบทุกคำขอ (allSettled) แล้วคืนผลแยกสำเร็จ/ล้มเหลวพร้อมข้อความจาก backend
 */
export interface BulkDeleteFailure<T> {
  id: T;
  message: string;
}

export interface BulkDeleteResult<T> {
  succeeded: T[];
  failed: BulkDeleteFailure<T>[];
}

export const bulkDeleteErrorMessage = (error: any): string => {
  const data = error?.response?.data;
  const detail = data?.detail;
  return (
    data?.error?.user_message ||
    data?.error?.message ||
    (typeof detail === 'string' ? detail : detail?.message) ||
    error?.message ||
    'เกิดข้อผิดพลาด'
  );
};

export async function runBulkDelete<T>(ids: T[], del: (id: T) => Promise<unknown>): Promise<BulkDeleteResult<T>> {
  const settled = await Promise.allSettled(ids.map((id) => del(id)));
  const result: BulkDeleteResult<T> = { succeeded: [], failed: [] };
  settled.forEach((s, i) => {
    if (s.status === 'fulfilled') result.succeeded.push(ids[i]);
    else result.failed.push({ id: ids[i], message: bulkDeleteErrorMessage(s.reason) });
  });
  return result;
}

const MAX_REASONS = 5;

/** ข้อความสรุปเมื่อมีรายการลบไม่ได้ — บอกจำนวนทั้งสองฝั่ง + เหตุผลจาก backend (ข้อความ BE มีชื่อรายการอยู่แล้ว) */
export function bulkDeleteFailureMessage<T>(entityLabel: string, result: BulkDeleteResult<T>): string {
  const head = result.succeeded.length
    ? `ลบ${entityLabel}สำเร็จ ${result.succeeded.length} รายการ · ลบไม่ได้ ${result.failed.length} รายการ`
    : `ลบ${entityLabel}ไม่ได้ ${result.failed.length} รายการ`;
  const reasons = result.failed.slice(0, MAX_REASONS).map((f) => `• ${f.message.split('\n')[0]}`);
  if (result.failed.length > MAX_REASONS) reasons.push(`• และอีก ${result.failed.length - MAX_REASONS} รายการ`);
  return [head, ...reasons].join('\n');
}
